import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { MotifButton, MotifIcon, MotifTooltip, MotifOption, MotifSelect, MotifToast, MotifProgressLoader, MotifModal, MotifModalBody, MotifModalFooter } from "@ey-xd/motif-react";
import { infoIcon } from "../../../common/MotifIcons";
import "./QualificationDialog.scss";
import debounce from 'lodash.debounce';
import Select from "react-select";
import { searchGraphUsers } from "../../../common/services/commonService";
import { editCompleteQualificationDetails, editEngagementQualification, fetchCompleteQualificationDetails, fetchEngagementQualification, fetchSSPQuestions } from "../redux/engagementQualificationSlice";
import logger from "../../../common/utils/logger";

const INITIAL_FORM_DATA = {
    engagementType: "Buy-side",
    targetEntity: "",
    clientEffectiveControl: null,
    allAssetsAcquired: null,
    clientDataRestrictions: null,
    dataTransferRestrictions: null,
    activeLegalHolds: null,
    nonClientData: null,
    dataUploaders: [],
    additionalComments: "",
    approveReuse: false,
    selectedValue: null,
};

const DEFAULT_USERS = [];
const TRANSACTION_STILL_LIVE_VALUE = "Transaction is still live";
const INACTIVE_UPLOADER_TOOLTIP = "This person is no longer a part of this organization.";
const PENDING_ENGAGEMENT_MESSAGE = `DBS can only re-use Buy-side engagement data once the transaction has closed and the client has taken effective control of the target. This engagement will be moved to “Pending”, please update this form once the transaction has closed.`;
const TIC_NOT_FOUND_MESSAGE = "This engagement does not qualify for EY-P DBS data reuse because the Target Information Clause (TIC) was not found.";
const COMPLETE_QUALIFICATION_PAGE = "complete_qualification_page";
const RESTRICTION_TOOLTIPS = [
    "The engagement/Client should not have any restrictions on the sharing/reuse of data, including in any side letters, that contradict and override the Client Information Consent Clause (CIC) clause.",
    "The Engagement/Client should not have any restrictions preventing the use of GDS or offshoring data.",
    "If there are any active legal holds on client information then this engagements should not be used in EY-P DBS at this time",
];

const normalizeQuestionId = (value, fallback = null) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
    return fallback;
};

const normalizeQuestion = (item, index = 0) => {
    if (typeof item === "string") {
        const question = String(item || "").replace(/\s+/g, " ").trim();
        if (!question) return null;
        return {
            questionId: index + 1,
            question,
            page: COMPLETE_QUALIFICATION_PAGE,
            questionInfo: "",
        };
    }

    if (!item || typeof item !== "object") return null;

    const question = String(item?.question || "").replace(/\s+/g, " ").trim();
    if (!question) return null;

    return {
        questionId: normalizeQuestionId(item?.questionId, index + 1),
        question,
        page: String(item?.page || "").toLowerCase().trim() || COMPLETE_QUALIFICATION_PAGE,
        questionInfo: String(item?.questionInfo || "").replace(/\s+/g, " ").trim(),
    };
};

const isBooleanAnswer = (value) => value === true || value === false;

const toBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "1" || normalized === "true" || normalized === "yes") return true;
    if (normalized === "0" || normalized === "false" || normalized === "no") return false;
    return null;
};

const toYesNo = (value) => {
    if (value === true) return "yes";
    if (value === false) return "no";
    return null;
};

const toTransactionClosedSelection = (value) => {
    if (String(value || "").trim() === TRANSACTION_STILL_LIVE_VALUE) {
        return TRANSACTION_STILL_LIVE_VALUE;
    }
    return toYesNo(toBoolean(value));
};

const toTransactionClosedPayload = (value) => {
    if (String(value || "").trim() === TRANSACTION_STILL_LIVE_VALUE) {
        return TRANSACTION_STILL_LIVE_VALUE;
    }
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "yes") return "yes";
    if (normalized === "no") return "no";
    return null;
};

const normalizeTransactionType = (value) => {
    const normalized = String(value || "").toLowerCase().trim();
    if (normalized === "tbd" || normalized === "to be determined") return "";
    if (normalized.includes("buy")) return "Buy-side";
    if (normalized.includes("sell")) return "Sell-side";
    if (normalized.includes("not") && normalized.includes("transaction")) return "Not a transaction";
    if (normalized === "other") return "Not a transaction";
    return "Buy-side";
};

const toTransactionType = (engagementType) => engagementType || "Buy-side";
const isTargetInfoClauseNotFound = (value) => String(value || "").replace(/\s+/g, " ").trim().toLowerCase() === "not found";

const parseDataUploaders = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return [];
    return raw
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
        .map((email) => ({
            label: email,
            value: email,
            image: null,
            email,
            isNameResolved: false,
            isGraphUserUnavailable: false,
        }));
};

const serializeDataUploaders = (uploaders = []) => {
    if (!Array.isArray(uploaders)) return "";
    return uploaders
        .map((item) => item?.email || item?.value || "")
        .map((value) => String(value).trim())
        .filter(Boolean)
        .join(",");
};

const serializeUploaderUserNames = (uploaders = []) => {
    if (!Array.isArray(uploaders)) return "";
    return uploaders
        .map((item) => {
            const email = String(item?.email || item?.value || "").trim().toLowerCase();
            const label = String(item?.label || "").trim();
            if (!label) return "";
            if (email && label.toLowerCase() === email) return "";
            return label;
        })
        .filter(Boolean)
        .join(",");
};

const getQuestionAnswerById = (questionAnswers = [], questionId) => {
    const normalizedId = normalizeQuestionId(questionId, null);
    if (!normalizedId || !Array.isArray(questionAnswers)) return null;
    const answer = questionAnswers.find((item) => normalizeQuestionId(item?.questionId, null) === normalizedId)?.answer;
    return isBooleanAnswer(toBoolean(answer)) ? toBoolean(answer) : null;
};

const mapDetailsToForm = (details = {}, fallbackTargetEntity = "", restrictionQuestions = []) => {
    const parsedUploaders = parseDataUploaders(details?.dataUploaders);
    const detailQuestionAnswers = Array.isArray(details?.questionAnswers) ? details.questionAnswers : [];
    const questionAnswersById = {};
    restrictionQuestions.forEach((question, index) => {
        const questionId = normalizeQuestionId(question?.questionId, index + 1);
        if (!questionId) return;

        const questionAnswer = getQuestionAnswerById(detailQuestionAnswers, questionId);
        if (questionAnswer === true || questionAnswer === false) {
            questionAnswersById[questionId] = questionAnswer;
            return;
        }

        const legacyValue = index === 0
            ? toBoolean(details?.canOverrideCICClause)
            : index === 1
                ? toBoolean(details?.canDataTransfer)
                : index === 2
                    ? toBoolean(details?.haveActiveLegalHold)
                    : null;
        questionAnswersById[questionId] = legacyValue;
    });

    return {
        ...INITIAL_FORM_DATA,
        engagementType: normalizeTransactionType(details?.transactionType),
        targetEntity: details?.targetEntity ?? fallbackTargetEntity ?? "",
        clientEffectiveControl: toTransactionClosedSelection(details?.isTransactionClosed),
        allAssetsAcquired: toYesNo(toBoolean(details?.isEvaluatedAssetAquired)),
        clientDataRestrictions: toBoolean(details?.canOverrideCICClause),
        dataTransferRestrictions: toBoolean(details?.canDataTransfer),
        activeLegalHolds: toBoolean(details?.haveActiveLegalHold),
        questionAnswersById,
        additionalComments: details?.comments || "",
        dataUploaders: parsedUploaders,
    };
};

export const QualificationDialog = ({ open, onClose, engagementData, onSubmitSuccess }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [selectedValue, setValue] = useState(DEFAULT_USERS);
    const [foundUsers, setFoundUsers] = useState([]);
    const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);
    const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);

    const engagementGuid = engagementData?.engagementId_GUID;
    const detailsByGuid = useSelector((state) => state?.engagementQualification?.completeQualificationDetailsByGuid || {});
    const detailsStatusByGuid = useSelector((state) => state?.engagementQualification?.completeQualificationStatusByGuid || {});
    const saveStatusByGuid = useSelector((state) => state?.engagementQualification?.completeQualificationSaveStatusByGuid || {});
    const restrictionQuestionsFromApi = useSelector((state) => state?.engagementQualification?.sspQuestions || []);
    const restrictionQuestionsStatus = useSelector((state) => state?.engagementQualification?.sspQuestionsStatus || "idle");

    const currentDetails = engagementGuid ? detailsByGuid[engagementGuid] : null;
    const detailsStatus = engagementGuid ? detailsStatusByGuid[engagementGuid] : "idle";
    const saveStatus = engagementGuid ? saveStatusByGuid[engagementGuid] : "idle";
    const isLoadingDetails = open && detailsStatus === "loading";
    const isLoadingQuestions = open && (restrictionQuestionsStatus === "loading" || restrictionQuestionsStatus === "idle");
    const isLoadingDialogData = isLoadingDetails || isLoadingQuestions;
    const isSavingDetails = saveStatus === "loading";

    const restrictionQuestions = useMemo(() => {
        if (!Array.isArray(restrictionQuestionsFromApi)) return [];
        return restrictionQuestionsFromApi
            .map((item, index) => normalizeQuestion(item, index))
            .filter((item) => item?.page === COMPLETE_QUALIFICATION_PAGE)
            .sort((first, second) => (first?.questionId || 0) - (second?.questionId || 0));
    }, [restrictionQuestionsFromApi]);

    const getRestrictionAnswer = (data, questionId) => {
        const value = data?.questionAnswersById?.[questionId];
        return isBooleanAnswer(value) ? value : null;
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const renderYesNoLocal = (value, onToggle, disabled) => {
        const isSet = value === true || value === false;
        const toggleClass = isSet ? (value ? "eq-toggle on" : "eq-toggle off") : "eq-toggle neutral";

        return (
            <div className={disabled ? "eq-yesno disabled" : "eq-yesno"}>
                <span className={isSet ? (value ? "eq-no inactive" : "eq-no") : "eq-no inactive"}>False</span>
                <button
                    type="button"
                    className={toggleClass}
                    aria-pressed={Boolean(value)}
                    onClick={onToggle}
                    disabled={disabled}
                />
                <span className={isSet ? (value ? "eq-yes" : "eq-yes inactive") : "eq-yes inactive"}>True</span>
            </div>
        );
    };

    useEffect(() => {
        if (!open) return;
        setFormData(INITIAL_FORM_DATA);
        setValue(DEFAULT_USERS);
        setFoundUsers([]);
        const shouldFetchQuestions =
            restrictionQuestionsStatus === "idle" ||
            !Array.isArray(restrictionQuestionsFromApi) ||
            restrictionQuestionsFromApi.length === 0;
        if (shouldFetchQuestions) {
            dispatch(fetchSSPQuestions());
        }
        if (engagementGuid && engagementData?.area) {
            dispatch(
                fetchCompleteQualificationDetails({
                    area: engagementData.area,
                    engagementId_GUID: engagementGuid,
                })
            );
        }
    }, [dispatch, engagementData?.area, engagementGuid, open, restrictionQuestionsFromApi, restrictionQuestionsStatus]);

    useEffect(() => {
        if (!open || !engagementGuid || !currentDetails) return;
        const mappedForm = mapDetailsToForm(currentDetails, engagementData?.targetEntity, restrictionQuestions);
        setFormData(mappedForm);
        setValue(mappedForm.dataUploaders || []);
    }, [currentDetails, engagementData?.targetEntity, engagementGuid, open, restrictionQuestions]);

    useEffect(() => {
        if (!open) return;
        setFormData((prev) => {
            const next = { ...prev };
            let changed = false;
            const nextQuestionAnswersById = { ...(next.questionAnswersById || {}) };
            restrictionQuestions.forEach((question, index) => {
                const questionId = normalizeQuestionId(question?.questionId, index + 1);
                if (!questionId) return;

                if (!Object.prototype.hasOwnProperty.call(nextQuestionAnswersById, questionId)) {
                    nextQuestionAnswersById[questionId] = null;
                    changed = true;
                } else if (!isBooleanAnswer(nextQuestionAnswersById[questionId]) && nextQuestionAnswersById[questionId] !== null) {
                    nextQuestionAnswersById[questionId] = null;
                    changed = true;
                }
            });
            if (changed) {
                next.questionAnswersById = nextQuestionAnswersById;
            }
            return changed ? next : prev;
        });
    }, [open, restrictionQuestions]);

    useEffect(() => {
        if (!open || !Array.isArray(selectedValue) || selectedValue.length === 0) return;

        const unresolvedUploaders = selectedValue.filter((item) => {
            const email = String(item?.email || item?.value || "").trim();
            const label = String(item?.label || "").trim();
            return !item?.isNameResolved && email && (!label || label.toLowerCase() === email.toLowerCase());
        });

        if (unresolvedUploaders.length === 0) return;

        let isMounted = true;

        const hydrateUploaderNames = async () => {
            const resolvedUploaders = await Promise.all(
                selectedValue.map(async (item) => {
                    const email = String(item?.email || item?.value || "").trim();
                    const label = String(item?.label || "").trim();
                    if (!email || (label && label.toLowerCase() !== email.toLowerCase())) {
                        return item;
                    }

                    try {
                        const results = await searchGraphUsers(email);
                        const exactMatch =
                            results?.find((user) => String(user?.status || "").toLowerCase() === email.toLowerCase()) ||
                            results?.[0];

                        return {
                            ...item,
                            value: email,
                            email,
                            label: exactMatch?.displayName || email,
                            image: exactMatch?.image || item?.image || null,
                            isNameResolved: true,
                            isGraphUserUnavailable: !exactMatch?.displayName,
                        };
                    } catch (error) {
                        return {
                            ...item,
                            value: email,
                            email,
                            label: email,
                            isNameResolved: true,
                            isGraphUserUnavailable: true,
                        };
                    }
                })
            );

            if (!isMounted) return;
            setValue(resolvedUploaders);
            setFormData((prev) => ({
                ...prev,
                dataUploaders: resolvedUploaders,
            }));
        };

        hydrateUploaderNames();

        return () => {
            isMounted = false;
        };
    }, [open, selectedValue]);

    const userOptions = Array.isArray(foundUsers)
        ? foundUsers.map((user) => ({
            label: user.displayName,
            value: user.email || user.status,
            image: user.image,
            email: user.email || user.status,
            isNameResolved: true,
            isGraphUserUnavailable: false,
        }))
        : [];
    const debouncedSearch = debounce(async (query) => {
        const results = await searchGraphUsers(query);
        setFoundUsers(results);

    }, 500);

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const isTransactionStillLive =
        formData.engagementType === "Buy-side" && formData.clientEffectiveControl === TRANSACTION_STILL_LIVE_VALUE;
    const isBuySideTicNotFound =
        formData.engagementType === "Buy-side" && isTargetInfoClauseNotFound(engagementData?.targetInfoClauseFound);

    useEffect(() => {
        if (formData.engagementType === "Buy-side") return;
        setFormData((prev) => {
            const restrictionAnswers = restrictionQuestions.map((question, index) => {
                const questionId = normalizeQuestionId(question?.questionId, index + 1);
                return getRestrictionAnswer(prev, questionId);
            });
            const areBuySideAndRestrictionFieldsAlreadyNull =
                prev.clientEffectiveControl === null &&
                prev.allAssetsAcquired === null &&
                restrictionAnswers.every((value) => value === null);
            if (areBuySideAndRestrictionFieldsAlreadyNull) return prev;
            const nextQuestionAnswersById = { ...(prev.questionAnswersById || {}) };
            restrictionQuestions.forEach((question, index) => {
                const questionId = normalizeQuestionId(question?.questionId, index + 1);
                if (!questionId) return;
                nextQuestionAnswersById[questionId] = null;
            });
            const next = {
                ...prev,
                clientEffectiveControl: null,
                allAssetsAcquired: null,
                questionAnswersById: nextQuestionAnswersById,
            };
            return {
                ...next,
            };
        });
    }, [formData.engagementType, restrictionQuestions]);

    useEffect(() => {
        const shouldResetRestrictionAnswers =
            formData.clientEffectiveControl === "no" || formData.allAssetsAcquired === "no";
        if (!shouldResetRestrictionAnswers) return;

        setFormData((prev) => {
            const areRestrictionAnswersAlreadyNull = restrictionQuestions
                .map((question, index) => {
                    const questionId = normalizeQuestionId(question?.questionId, index + 1);
                    return getRestrictionAnswer(prev, questionId);
                })
                .every((value) => value === null);
            if (areRestrictionAnswersAlreadyNull) return prev;
            const nextQuestionAnswersById = { ...(prev.questionAnswersById || {}) };
            restrictionQuestions.forEach((question, index) => {
                const questionId = normalizeQuestionId(question?.questionId, index + 1);
                if (!questionId) return;
                nextQuestionAnswersById[questionId] = null;
            });
            return {
                ...prev,
                questionAnswersById: nextQuestionAnswersById,
            };
        });
    }, [formData.clientEffectiveControl, formData.allAssetsAcquired, restrictionQuestions]);

    const buildSavePayload = () => {
        const uploaderCsv = serializeDataUploaders(formData.dataUploaders || selectedValue);
        const isBuySideDropdownRestrictedForPayload =
            formData.engagementType === "Buy-side" &&
            (formData.clientEffectiveControl === "no" || formData.allAssetsAcquired === "no");
        const restrictionAnswers = restrictionQuestions.map((question, index) => {
            const questionId = normalizeQuestionId(question?.questionId, index + 1);
            return getRestrictionAnswer(formData, questionId);
        });
        const isEngagementRestrictedForPayload =
            isBuySideDropdownRestrictedForPayload ||
            restrictionAnswers.some((value) => value === false) ||
            isBuySideTicNotFound;
        const questionAnswers = restrictionQuestions
            .map((question, index) => {
                const questionId = normalizeQuestionId(question?.questionId, index + 1);
                const answer = getRestrictionAnswer(formData, questionId);
                if (!questionId || (answer !== true && answer !== false)) return null;
                return {
                    questionId,
                    answer,
                    engagementIdGuid: engagementGuid || "",
                };
            })
            .filter(Boolean);

        return {
            engagementId_GUID: engagementGuid || "",
            transactionType: toTransactionType(formData.engagementType),
            isTransactionClosed: toTransactionClosedPayload(formData.clientEffectiveControl),
            isEvaluatedAssetAquired: toBoolean(formData.allAssetsAcquired),
            isClientData: !isTransactionStillLive && isEngagementRestrictedForPayload ? false : true,
            canOverrideCICClause: restrictionAnswers[0] === null ? null : Boolean(restrictionAnswers[0]),
            canDataTransfer: restrictionAnswers[1] === null ? null : Boolean(restrictionAnswers[1]),
            haveActiveLegalHold: restrictionAnswers[2] === null ? null : Boolean(restrictionAnswers[2]),
            questionAnswers,
            comments: formData.additionalComments || "",
            dataUploaders: uploaderCsv,
        };
    };

    const buildEngagementDecisionPayload = (finalDecision) => {
        const uploaders = Array.isArray(formData?.dataUploaders) ? formData.dataUploaders : selectedValue;
        const uploaderCsv = serializeDataUploaders(uploaders);
        const userNamesCsv = serializeUploaderUserNames(uploaders);
        const isQualified = String(finalDecision || "").toLowerCase() === "qualified";

        return {
            engagementId_GUID: engagementGuid || "",
            questionAnswers: (Array.isArray(engagementData?.questionAnswers) ? engagementData.questionAnswers : [])
                .filter((item) => item?.answer === true || item?.answer === false)
                .map((item, index) => ({
                    questionId: normalizeQuestionId(item?.questionId, index + 1),
                    answer: item?.answer === true,
                    engagementIdGuid: engagementGuid || "",
                }))
                .filter((item) => item?.questionId),
            finalDecision,
            dataUploaders: isQualified ? uploaderCsv : null,
            userNames: isQualified ? userNamesCsv : null,
        };
    };

    const handleSave = async ({ submit = false } = {}) => {
        if (!engagementGuid || !engagementData?.area || isSavingDetails || (submit && isSubmitInProgress)) return;

        const payload = buildSavePayload();
        if (submit) {
            setIsSubmitInProgress(true);
        }

        try {
            const saveAction = dispatch(
                editCompleteQualificationDetails({
                    area: engagementData.area,
                    payload,
                })
            );
            if (typeof saveAction?.unwrap === "function") {
                await saveAction.unwrap();
            } else {
                await saveAction;
            }

            if (submit) {
                const finalDecision = isBuySideTicNotFound ? "Not Qualified" : (isTransactionStillLive ? "Pending" : "Qualified");
                const statusAction = dispatch(
                    editEngagementQualification({
                        area: engagementData.area,
                        payload: buildEngagementDecisionPayload(finalDecision),
                    })
                );
                if (typeof statusAction?.unwrap === "function") {
                    await statusAction.unwrap();
                } else {
                    await statusAction;
                }

                if (finalDecision === "Qualified" && typeof onSubmitSuccess === "function") {
                    onSubmitSuccess({
                        engagementId: engagementData?.engagementId || "",
                        engagementId_GUID: engagementGuid || "",
                        finalDecision,
                    });
                }
                onClose();
                dispatch(fetchEngagementQualification());
                return;
            }

            onClose();
        } catch (error) {
            logger.error("Failed to save complete qualification details:", error);
            setApiErrorDialogOpen(true);
        } finally {
            if (submit) {
                setIsSubmitInProgress(false);
            }
        }
    };

    const handleSaveDraft = () => {
        handleSave({ submit: false });
    };

    const handleSubmit = () => {
        handleSave({ submit: true });
    };
    const handleApiErrorDialogClose = () => {
        setApiErrorDialogOpen(false);
    };

    const getInitials = (name) => {
        if (name === null) return '';
        const words = name.trim().split(/\s+/);
        if (words.length === 0) return '';
        const first = words[0][0];
        const last = words[words.length - 1][0];
        return (first + last).toUpperCase();
    }

    const isBuySideDropdownRestricted =
        formData.engagementType === "Buy-side" &&
        (formData.clientEffectiveControl === "no" || formData.allAssetsAcquired === "no");
    const restrictionAnswers = restrictionQuestions.map((question, index) => {
        const questionId = normalizeQuestionId(question?.questionId, index + 1);
        return getRestrictionAnswer(formData, questionId);
    });
    const hasRestrictionQuestions = restrictionQuestions.length > 0;

    const isEngagementEligible =
        hasRestrictionQuestions &&
        restrictionAnswers.every((value) => value === true) &&
        !isBuySideDropdownRestricted;

    const isEngagementRestricted =
        isBuySideTicNotFound ||
        isBuySideDropdownRestricted ||
        (hasRestrictionQuestions && restrictionAnswers.some((value) => value === false));

    const restrictionQuestionsDisabled =
        formData.clientEffectiveControl === "no" || formData.allAssetsAcquired === "no" || isBuySideTicNotFound;

    const areRestrictionQuestionsAnswered =
        hasRestrictionQuestions && restrictionAnswers.every((value) => value === true || value === false);

    const isAllAssetsSelectionProvided =
        formData.allAssetsAcquired === "yes" || formData.allAssetsAcquired === "no";

    const isApproveCriteriaMet = (() => {
        if (formData.engagementType !== "Buy-side") {
            return areRestrictionQuestionsAnswered;
        }

        if (isTransactionStillLive) return false;

        if (formData.clientEffectiveControl === "yes") {
            if (!isAllAssetsSelectionProvided) return false;
            if (formData.allAssetsAcquired === "no") return true;
            return areRestrictionQuestionsAnswered;
        }

        if (formData.clientEffectiveControl === "no") {
            return isAllAssetsSelectionProvided;
        }

        return false;
    })();

    const isApproveCheckboxDisabled =
        formData.nonClientData === false ||
        isTransactionStillLive ||
        isBuySideTicNotFound ||
        isSavingDetails ||
        isLoadingDialogData ||
        !isApproveCriteriaMet;

    useEffect(() => {
        const shouldClearApproval = (formData.nonClientData === false || isBuySideTicNotFound || !isApproveCriteriaMet) && formData.approveReuse;
        if (!shouldClearApproval) return;
        setFormData((prev) => ({
            ...prev,
            approveReuse: false,
        }));
    }, [formData.approveReuse, formData.nonClientData, isApproveCriteriaMet, isBuySideTicNotFound]);

    const canSubmitWithoutApproval = isTransactionStillLive || isBuySideTicNotFound;
    const isSubmitDisabled =
        isSavingDetails ||
        isSubmitInProgress ||
        isLoadingDialogData ||
        (!canSubmitWithoutApproval && (isApproveCheckboxDisabled || !formData.approveReuse));

   
    return (
        <>
        <Dialog open={open} onClose={(isSavingDetails || isSubmitInProgress) ? undefined : onClose} aria-labelledby="qualification-dialog" maxWidth="sm" fullWidth>
            <DialogTitle id="qualification-dialog" style={{padding:'10px 20px'}}>
                <div className="qualification-dialog-title">
                    <span>Complete engagement qualification details.</span>
                    <IconButton onClick={onClose} size="small" disabled={isSavingDetails || isSubmitInProgress}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent dividers style={{padding:'10px 20px'}}>
                {isLoadingDialogData ? (
                    <div className="qual-loader-wrap">
                        <MotifProgressLoader show variant="default" />
                    </div>
                ) : (
                <>
                {/* Engagement Details Section */}
                <div className="qual-section">
                    <div className="qual-details-grid">
                        <div className="qual-detail-item">
                            <label>Engagement ID</label>
                            <div>{engagementData?.engagementId || "-"}</div>
                        </div>
                        <div className="qual-detail-item">
                            <label>Engagement Name</label>
                            <div>{engagementData?.engagementName || "-"}</div>
                        </div>
                        <div className="qual-detail-item">
                            <label>Client Name</label>
                            <div>{engagementData?.clientName || "-"}</div>
                        </div>
                        <div className="qual-detail-item">
                            <label>Date Mercury code opened</label>
                            <div>{engagementData?.dataMercuryCodeOpened?.split(" ")[0] || "-"}</div>
                        </div>
                    </div>
                </div>

                <div className="qual-form">
                    <div className="qual-form-group">
                        <div className="qual-form-label">
                            <span>What is the Engagement Type?</span>
                            <MotifTooltip
                                allowClick
                                allowHover
                                closeButtonProps={{ style: { display: 'none' } }}
                                flip
                                id="tooltip"
                                placement="auto"
                                trigger={
                                    <MotifIcon
                                        src={infoIcon}
                                    />
                                }
                                variant="alt"
                            >
                                <div style={{ maxWidth: 280 }}>There are additional checks for Buy-side engagements since the data used is typically on a Target entity.</div>
                            </MotifTooltip>

                        </div>
                        <div className="qual-yesno-select">
                            <MotifSelect
                                ariaLabelledBy="engagement-type-select"
                                onChange={(value) => handleInputChange("engagementType", value)}
                                placeholder="Select"
                                visibleOptions={3}
                                value={formData.engagementType ?? ""}
                                style={{ width: "50%" }}
                                className="qual-select-placeholder-only"
                                disabled={isSavingDetails}
                            >
                                <MotifOption value="Buy-side">Buy-side</MotifOption>
                                <MotifOption value="Sell-side">Sell-side</MotifOption>
                                <MotifOption value="Not a transaction">Not a transaction</MotifOption>
                            </MotifSelect>
                        </div>

                        {formData.engagementType === "Buy-side" && (
                            <>
                                <div className="qual-form-label" style={{ marginTop: 8 }}>
                                    <span>Target Entity</span>
                                </div>
                                <input
                                    type="text"
                                    className="qual-input qual-control-half qual-input-readonly"
                                    value={formData.targetEntity || ""}
                                    title={formData.targetEntity || "-"}
                                    readOnly
                                    aria-readonly="true"
                                    disabled={isSavingDetails || isBuySideTicNotFound}
                                />
                            </>
                        )}
                    </div>

 {formData.engagementType === "Buy-side" && (
                        <div className="qual-form-group">
                            <div className="qual-form-label">
                                <span>Transaction closed. Client has taken Effective control.</span>
                                <MotifTooltip
                                    allowClick
                                    allowHover
                                    closeButtonProps={{ style: { display: 'none' } }}
                                    flip
                                    id="effectiveControlTooltip"
                                    placement="auto"
                                    trigger={
                                        <MotifIcon
                                            src={infoIcon}
                                        />
                                    }
                                    variant="alt"
                                >
                                   <div style={{ maxWidth: 280 }}>{`Did the client take effective control (>50%) of the target entity? This check is to ensure that the Client now owns the rights to the Target entity's data.`}</div>
                                </MotifTooltip>
                            </div>
                            <div className="qual-yesno-select">
                                <MotifSelect
                                    ariaLabelledBy="select-label-default"
                                    onChange={(value) => handleInputChange("clientEffectiveControl", value)}
                                    placeholder="Select"
                                    visibleOptions={4}
                                    value={formData.clientEffectiveControl ?? ""}
                                    style={{ width: "50%" }}
                                    className="qual-select-placeholder-only"
                                    disabled={isSavingDetails || isBuySideTicNotFound}
                                >
                                    <MotifOption value="yes">Yes</MotifOption>
                                    <MotifOption value="no">No</MotifOption>
                                    <MotifOption value={TRANSACTION_STILL_LIVE_VALUE}>Transaction is still live</MotifOption>
                                </MotifSelect>
                            </div>

                            <div className="qual-form-label" style={{ marginTop: 8 }}>
                                <span>All evaluated assets, entities, etc., acquired</span>
                                <MotifTooltip
                                    allowClick
                                    allowHover
                                    closeButtonProps={{ style: { display: 'none' } }}
                                    flip
                                    id="assetsAcquiredTooltip"
                                    placement="auto"
                                    trigger={
                                        <MotifIcon
                                            src={infoIcon}
                                        />
                                    }
                                    variant="alt"
                                >
                                    <div style={{ maxWidth: 280 }}>Did the client acquire all of the evaluated targets? (there should be no data on non acquired assets, entities, etc.). Any data on non acquired assets/entities should be excluded from EY-P DBS.</div>
                                </MotifTooltip>
                            </div>
                            <div className="qual-yesno-select">
                                <MotifSelect
                                    ariaLabelledBy="select-label-default"
                                    onChange={(value) => handleInputChange("allAssetsAcquired", value)}
                                    placeholder="Select"
                                    visibleOptions={3}
                                    value={formData.allAssetsAcquired ?? ""}
                                    style={{ width: "50%" }}
                                    className="qual-select-placeholder-only qual-all-assets-select"
                                    disabled={isSavingDetails || isBuySideTicNotFound}
                                >
                                    <MotifOption value="yes">Yes</MotifOption>
                                    <MotifOption value="no">No</MotifOption>
                                </MotifSelect>
                            </div>

                          
                            </div>
                             )}

                    {/* Dynamic restriction questions - always visible, disabled if buy-side questions answered No */}
                    <div className="qual-form-group">
                        {restrictionQuestions.map((question, index) => {
                            const questionId = normalizeQuestionId(question?.questionId, index + 1);
                            const value = getRestrictionAnswer(formData, questionId);
                            const tooltipText = String(question?.questionInfo || RESTRICTION_TOOLTIPS[index] || "").trim();
                            const questionText = String(question?.question || "");
                            const questionWords = questionText.split(" ");
                            const lastWord = questionWords.length > 0 ? questionWords[questionWords.length - 1] : "";
                            const headText = questionWords.slice(0, -1).join(" ");
                            return (
                                <div
                                    key={`restriction-question-${questionId}-${index}`}
                                    className={`qual-form-label qual-restriction-question ${restrictionQuestionsDisabled ? "is-disabled" : ""}`}
                                    style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
                                >
                                    <span className="qual-label-with-icon">
                                        {tooltipText ? (
                                            <>
                                                {headText ? (
                                                    <span className="qual-label-text">{`${headText} `}</span>
                                                ) : null}
                                                <span className="qual-label-tail-with-tooltip">
                                                    {lastWord}
                                                    <span className="qual-label-tooltip-inline">
                                                        <MotifTooltip
                                                            allowClick
                                                            allowHover
                                                            closeButtonProps={{ style: { display: 'none' } }}
                                                            flip
                                                            id={`restrictionQuestionTooltip-${index}`}
                                                            placement="auto"
                                                            trigger={<MotifIcon src={infoIcon} />}
                                                            variant="alt"
                                                        >
                                                            <div style={{ maxWidth: 280 }}>{tooltipText}</div>
                                                        </MotifTooltip>
                                                    </span>
                                                </span>
                                            </>
                                        ) : (
                                            <span className="qual-label-text">{questionText}</span>
                                        )}
                                    </span>
                                    {renderYesNoLocal(
                                        value,
                                        () => {
                                            const disabled = restrictionQuestionsDisabled;
                                            if (isBuySideTicNotFound && disabled) return;
                                            if (disabled && value !== false) return;
                                            const nextValue = value === true ? false : true;
                                            setFormData((prev) => ({
                                                ...prev,
                                                questionAnswersById: {
                                                    ...(prev.questionAnswersById || {}),
                                                    [questionId]: nextValue,
                                                },
                                            }));
                                        },
                                        restrictionQuestionsDisabled
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {isBuySideTicNotFound ? (
                        <MotifToast
                            variant="error"
                            className="eligibility-error-toast tic-not-found-toast"
                            closeBtnProps={{ style: { display: 'none', padding: 0 } }}
                        >
                            <div>{TIC_NOT_FOUND_MESSAGE}</div>
                        </MotifToast>
                    ) : isTransactionStillLive ? (
                        <MotifToast
                            variant="error"
                            className="eligibility-error-toast"
                            closeBtnProps={{ style: { display: 'none', padding: 0 } }}
                        >
                            <div>{PENDING_ENGAGEMENT_MESSAGE}</div>
                        </MotifToast>
                    ) : isEngagementRestricted ? (
                        <MotifToast
                            variant="error"
                            className="eligibility-error-toast non-client-data-toast"
                            closeBtnProps={{ style: { display: 'none', padding: 0 } }}
                        >
                            <div>This Engagement is restricted from client data uploads. You can upload Non-Client data only.</div>
                        </MotifToast>
                    ) : isEngagementEligible ? (
                        <MotifToast
                            variant="success"
                            className="eligibility-success-toast"
                            closeBtnProps={{ style: { display: 'none', padding: 0 } }}
                        >
                            <div>The engagement is eligible to be transmitted to EY-P DBS.</div>
                        </MotifToast>
                    ) : null}

                    {/* Data Uploaders */}
                    <div className={`qual-form-group ${formData.nonClientData === false || isTransactionStillLive ? "qual-uploaders-disabled" : ""}`}>
                        <div className="qual-form-label">
                            <span>Add Data Uploaders</span>
                        </div>
                        <div className="motif-col-lg-10 custom-motif-col-lg-10 qual-control-half qual-data-uploaders">
                            <Select
                                className="motif-select"
                                classNamePrefix="qual-react-select"
                                isMulti
                                isDisabled={formData.nonClientData === false || isTransactionStillLive || isSavingDetails || isBuySideTicNotFound}
                                value={selectedValue}
                                onInputChange={(inputValue) => {
                                    debouncedSearch(inputValue);
                                }}
                                options={userOptions}
                                onChange={(val) => {
                                    const nextValue = Array.isArray(val) ? val : [];
                                    setValue(nextValue);
                                    handleInputChange("dataUploaders", nextValue);
                                }}
                                formatOptionLabel={(data, { context }) => {
                                    if (context === "menu") {
                                        return (
                                            <div className="image-wrapper">
                                                {data.image ? <img
                                                    src={data.image}
                                                    alt=""
                                                    style={{ width: 24, height: 24, borderRadius: '50%', marginRight: 8 }}
                                                /> :
                                                    <div className="avatar-wrapper">
                                                        <div className="avatar">{getInitials(data.label)}</div>
                                                    </div>}
                                                <div>
                                                    <div>{data.label}</div>
                                                    <small style={{ color: '#666' }}>{data.value}</small>
                                                </div>
                                            </div>
                                        );
                                    }
                                    const unresolvedEmail = String(data?.email || data?.value || "").trim();
                                    if (data?.isGraphUserUnavailable && unresolvedEmail) {
                                        return (
                                            <span
                                                className="qual-uploader-email-inactive"
                                                title={INACTIVE_UPLOADER_TOOLTIP}
                                            >
                                                {unresolvedEmail}
                                            </span>
                                        );
                                    }
                                    return data.label;
                                }}
                            />
                        </div>
                    </div>

                    {/* Additional Comments */}
                    <div className="qual-form-group">
                        <label className="qual-form-label">Additional Comments</label>
                        <textarea
                            className="qual-textarea"
                            placeholder="Type your comment"
                            value={formData.additionalComments}
                            onChange={(e) => handleInputChange("additionalComments", e.target.value)}
                            rows={3}
                            disabled={isSavingDetails || isBuySideTicNotFound}
                        />
                    </div>

                </div>
                </>
                )}
            </DialogContent>
            <DialogActions className="qual-footer-actions">
                <div className="qual-checkbox-group qual-footer-approval">
                    <label>
                        <input
                            type="checkbox"
                            checked={formData.approveReuse}
                            onChange={(e) => handleInputChange("approveReuse", e.target.checked)}
                            disabled={isApproveCheckboxDisabled}
                        />
                        <span>I approve the reuse of data for this engagement.</span>
                    </label>
                </div>
                <div className="qual-footer-buttons">
                    <MotifButton onClick={handleSaveDraft} variant="secondary" size="medium" type="button" disabled={isSavingDetails || isSubmitInProgress || isLoadingDialogData || isBuySideTicNotFound}>
                        Save draft
                    </MotifButton>
                    {isSubmitInProgress ? (
                        <div className="qual-submit-inline-loader">
                            <MotifProgressLoader show variant="default" />
                        </div>
                    ) : null}
                    <MotifButton onClick={handleSubmit} size="medium" type="button" disabled={isSubmitDisabled}>
                        Submit
                    </MotifButton>
                </div>
            </DialogActions>
        </Dialog>
        <MotifModal
            show={apiErrorDialogOpen}
            onClose={handleApiErrorDialogClose}
            size="sm"
            className="qual-api-error-dialog"
        >
            <MotifModalBody>
                <p>something went wrong, please try again</p>
            </MotifModalBody>
            <MotifModalFooter>
                <MotifButton onClick={handleApiErrorDialogClose} size="medium" type="button">
                    OK
                </MotifButton>
            </MotifModalFooter>
        </MotifModal>
        </>
    );
};
